import { useEffect, useState } from "react";

import { message, Table, Tag, Tooltip } from "antd";
import { ColumnType, TablePaginationConfig } from "antd/lib/table";
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from "antd/es/table/interface";
import denormalizeTaxNumber from "denormalize-steuernummer";
import { capitalize } from "lodash";
import { useTranslation } from "react-i18next";

import type { EditableAccountAttributes } from "@/v1/types/IAccount.type";
import {
  IUser,
  IUserDetails,
  KontaxUserStatus,
  KontaxUserStatusAdditionalParams,
  TableParams,
  UserRow,
} from "../../../../../types";
import { getColorByUserStatus } from "../../../../../utils";
import { formatAssignee } from "../../../../../utils/assignee";
import AssigneeFilter from "../../../../common/AssigneeFilter";
import Copyable from "../../../../common/Copyable";
import { formatDayMonthYear } from "../../Common/UserDetails/utils";
import UserDetails from "../UserDetails";
import { UserFilterOptions } from "../../Common/UserFilters";
import { ASSIGNEE_FILTER_KEY, CLIENT_STATE_KEY } from "../viewStateParams";
import { FIRM_FILTER_KEY } from "../viewStateParams";
import { FIRM_LABELS, FIRMS } from "@/modules/firms";

const copyToClipboard = (email: string, successMessage: string) => {
  navigator.clipboard.writeText(email);
  message.success(successMessage);
};

const clientStateFilters: { text: string; value: string }[] = [];
for (const key in KontaxUserStatus) {
  clientStateFilters.push({
    text: capitalize(key),
    value: key,
  });
}

const setDefaultExpandedRowKeys = (dataSource: UserRow[]) =>
  dataSource.length === 1 ? [0] : [];

const UsersTable = ({
  users,
  updateUserStatus,
  updateDependents,
  deleteDependent,
  editUser,
  editAccount,
  setEmailParam,
  tableParams,
  onChange,
  setFilterOptions,
  refreshUser,
  filterOptions,
}: {
  users: Array<IUser>;
  updateUserStatus: (
    email: string,
    status: KontaxUserStatus | null,
    additionalParams?: KontaxUserStatusAdditionalParams
  ) => Promise<void>;
  updateDependents: Function;
  deleteDependent: (email: string, id: string) => Promise<void>;
  editUser: (email: string, userDetails: IUserDetails) => Promise<void>;
  editAccount: (
    id: number,
    accountDetails: EditableAccountAttributes
  ) => Promise<void>;
  setEmailParam: (email: string) => void;
  tableParams: TableParams;
  onChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<UserRow> | SorterResult<UserRow>[],
    extra: TableCurrentDataSource<UserRow>
  ) => void;
  setFilterOptions: (filterOptions: UserFilterOptions) => void;
  refreshUser: (email: string) => Promise<void>;
  filterOptions: UserFilterOptions;
}) => {
  const { t } = useTranslation();
  let dataSource: UserRow[] = users.map((user: IUser, index) => {
    return {
      key: index,
      name: user.legalName || "-",
      mobileNumber: user.mobileNumber || user.untrustedPhoneNumber,
      fibuOnboardingCompletedAt: formatDayMonthYear(
        user.taxServiceOnboardingCompletedAt
      ),
      user: user,
    };
  });

  const columns: ColumnType<UserRow>[] = [
    {
      title: "Account ID",
      dataIndex: ["user", "accountId"],
      width: 100,
      className: "accountId",
      showSorterTooltip: false,
    },
    {
      title: "Name",
      dataIndex: "name",
      width: 120,
      showSorterTooltip: false,
      sorter: false,
    },
    {
      title: "Phone number",
      dataIndex: ["user", "mobileNumber"],
      render: (value) => <Copyable>{value}</Copyable>,
      width: 110,
      showSorterTooltip: false,
      sorter: true,
    },
    {
      title: "Email",
      dataIndex: ["user", "email"],
      render: (value) => (
        <Tooltip title={value} placement="topLeft">
          <Copyable ellipsis>{value}</Copyable>
        </Tooltip>
      ),
      onCell: (record: UserRow) => {
        return {
          onClick: (event: any) => {
            event.stopPropagation();
            if (record.user.email)
              copyToClipboard(record.user.email, "Email copied");
          },
        };
      },
      width: 90,
      ellipsis: {
        showTitle: false,
      },
      showSorterTooltip: false,
      sorter: true,
    },
    {
      title: "Hauptsteuernr.",
      dataIndex: ["user", "taxNumber"],
      width: 110,
      showSorterTooltip: false,
      render: (value) => value && denormalizeTaxNumber(value)?.taxNumber,
    },
    {
      title: "Steuerliche Identifikationsnr.",
      dataIndex: ["user", "deTaxId"],
      width: 120,
      showSorterTooltip: false,
      sorter: true,
    },
    {
      title: "Client state",
      // antd derives the filter key from the joined dataIndex ("user.kontaxStatus")
      // and `handleTableChange` stores the selection under that key. Set the same
      // key explicitly and bind `filteredValue` to it so the selection is
      // controlled and persists across re-renders, pagination, sort and refresh
      // (SMT-1247). Filtering itself is server-side (getUsers), so no `onFilter`.
      key: CLIENT_STATE_KEY,
      className: "clientState",
      dataIndex: ["user", "kontaxStatus"],
      render: (value: KontaxUserStatus) => (
        <Tag color={getColorByUserStatus(value)} style={{ marginRight: 0 }}>
          {value}
        </Tag>
      ),
      width: 140,
      showSorterTooltip: false,
      align: "center",
      filters: clientStateFilters.sort((a, b) => a.text.localeCompare(b.text)),
      filteredValue: tableParams.filters?.[CLIENT_STATE_KEY] ?? null,
      sorter: true,
    },
    {
      title: t("firm"),
      key: FIRM_FILTER_KEY,
      dataIndex: ["user", "firm"],
      width: 120,
      render: (firm: IUser["firm"]) =>
        firm ? FIRM_LABELS[firm] : FIRM_LABELS.smarta,
      filters: FIRMS.map((firm) => ({
        text: FIRM_LABELS[firm],
        value: firm,
      })),
      filterMultiple: false,
      filteredValue:
        filterOptions.firm && filterOptions.firm !== "all"
          ? [filterOptions.firm]
          : null,
    },
    {
      title: t("assignee") || "Assignee",
      key: ASSIGNEE_FILTER_KEY,
      dataIndex: ["user", "kontaxUser"],
      width: 120,
      showSorterTooltip: false,
      render: (kontaxUser: IUser["kontaxUser"]) =>
        formatAssignee(
          kontaxUser?.assigneeDisplayName,
          kontaxUser?.assigneeEmail
        ),
      // Controlled like the Client-state column so antd does not treat the table
      // as partially controlled (SMT-1247). The active assignee lives in
      // filterOptions and is sent via the separate `assigneeId` param, so this
      // key is stripped from the backend `filters` payload in UserList.
      filteredValue: filterOptions.assigneeId
        ? [filterOptions.assigneeId]
        : null,
      filterDropdown: ({ confirm }) => (
        <div style={{ padding: 8 }}>
          <AssigneeFilter
            value={filterOptions.assigneeId ?? undefined}
            onChange={(val) => {
              setFilterOptions({
                ...filterOptions,
                assigneeId: val,
                myCases: false,
              });
              confirm();
            }}
          />
        </div>
      ),
    },
    {
      title: "FiBu onboarding completed",
      dataIndex: "fibuOnboardingCompletedAt",
      width: 140,
      showSorterTooltip: false,
    },
  ];

  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>(
    setDefaultExpandedRowKeys(dataSource)
  );

  useEffect(() => {
    if (expandedRowKeys.length) {
      document
        .querySelector(`tr[data-row-key="${expandedRowKeys[0]}"]`)
        ?.scrollIntoView({
          behavior: "smooth",
        });
    }
  }, [expandedRowKeys]);

  useEffect(() => {
    if (users.length === 1) setEmailParam(users[0].email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const onTableRowExpand = (expanded: boolean, record: UserRow) => {
    setExpandedRowKeys(expanded ? [record.key] : []);
    setEmailParam(record.user.email);
  };

  return (
    <Table
      dataSource={dataSource}
      pagination={tableParams.pagination}
      onChange={onChange}
      data-test="mandantenTable"
      columns={columns}
      bordered
      expandRowByClick
      scroll={{ x: "100%" }}
      rowClassName="expendableRow"
      expandable={{
        expandedRowRender: (record: UserRow) => (
          <UserDetails
            user={record.user}
            updateUserStatus={updateUserStatus}
            editUser={editUser}
            editAccount={editAccount}
            updateDependents={updateDependents}
            deleteDependent={deleteDependent}
            setEmailParam={setEmailParam}
            setFilterOptions={setFilterOptions}
            refreshUser={refreshUser}
          />
        ),
        defaultExpandedRowKeys: setDefaultExpandedRowKeys(dataSource),
        expandedRowKeys: expandedRowKeys,
        onExpand: onTableRowExpand,
      }}
    />
  );
};

export default UsersTable;
